import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RecruitingActions } from '../actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as directOfferImportMarginSelectors from '../selectors/direct-offer-import-margin.selector';
import { LookupService } from 'src/app/core/lookup.service';
import { ExpertiseModel } from 'src/app/shared/models/expertisemodel.model';
import { DirectOfferMarginService } from 'src/app/recruiting/direct-offer-margin/direct-offer-margin.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class DirectOfferImportMarginEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly directOfferMarginService: DirectOfferMarginService,
        private readonly lookupService: LookupService,
        private readonly _store: Store
    ) {}

    loadGridData$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(RecruitingActions.DirectOfferImportMarginActions.setMarginsGridSearchQuery),
                concatLatestFrom(() =>
                    this._store.pipe(select(directOfferImportMarginSelectors.selectMarginsGridSearchQuery))
                ),
                switchMap(([, query]) => {
                    return forkJoin([
                        this.directOfferMarginService.getImportMarginGridGridData(query),
                        this.lookupService.getExpertiseList()
                    ]).pipe(
                        switchMap((joinResult: any) => {
                            const [margins, expertiseList] = joinResult;
                            return [
                                RecruitingActions.DirectOfferImportMarginActions.loadMarginsSuccess({ margins }),
                                RecruitingActions.DirectOfferImportMarginActions.loadMarginsLookupsSuccess({
                                    expertiseList
                                })
                            ];
                        }),
                        catchError((error) =>
                            of(RecruitingActions.DirectOfferImportMarginActions.loadMarginsFailure({ error }))
                        )
                    );
                })
            ),
        { dispatch: true }
    );

    getExpertiseList$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DirectOfferImportMarginActions.loadMarginsLookups),
            switchMap(() =>
                this.lookupService.getExpertiseList().pipe(
                    map((expertiseList: ExpertiseModel[]) =>
                        RecruitingActions.DirectOfferImportMarginActions.loadMarginsLookupsSuccess({ expertiseList })
                    ),
                    catchError((error) =>
                        of(RecruitingActions.DirectOfferImportMarginActions.loadMarginsLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    createDirectOfferMarginViaImport$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImport),
            switchMap((action: { offerId: number; importFromMarginId: number }) =>
                this.directOfferMarginService
                    .createDirectOfferMarginViaImport(action.offerId, action.importFromMarginId)
                    .pipe(
                        map((marginId: number) =>
                            RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImportSuccess({
                                marginId
                            })
                        ),
                        catchError((error) =>
                            of(
                                RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImportFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    displayToaster$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImportFailure),
                map((action) =>
                    this._store.dispatch(
                        showToasterMessage({
                            // @ts-ignore
                            message: action.error.error,
                            duration: 5000
                        })
                    )
                )
            ),
        { dispatch: false }
    );
}
